const siteUrl = process.env.NEXT_PUBLIC_SITE_URL || "https://monkeyscode.com";

export function OrganizationJsonLd() {
  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{
        __html: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Organization",
          name: "MonkeysCode",
          legalName: "Colibricode Inc.",
          url: siteUrl,
          logo: `${siteUrl}/favicon-32x32.png`,
          description:
            "MonkeysCode is the agentic AI IDE with its own coding model, Capuchin — plus Claude, Gemini, and ChatGPT in one plan. Local-first, BYOK-friendly, no lock-in.",
          address: {
            "@type": "PostalAddress",
            addressLocality: "Denver",
            addressRegion: "CO",
            addressCountry: "US",
          },
        }),
      }}
    />
  );
}

export function WebSiteJsonLd() {
  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{
        __html: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "WebSite",
          name: "MonkeysCode",
          url: siteUrl,
        }),
      }}
    />
  );
}

export function SoftwareApplicationJsonLd() {
  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{
        __html: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "SoftwareApplication",
          name: "MonkeysCode",
          operatingSystem: ["macOS", "Windows", "Linux"],
          applicationCategory: "DeveloperApplication",
          applicationSubCategory: "IDE",
          offers: {
            "@type": "AggregateOffer",
            lowPrice: "0",
            highPrice: "200",
            priceCurrency: "USD",
            offerCount: "4",
            offers: [
              { "@type": "Offer", name: "Free", price: "0", priceCurrency: "USD", description: "Full agentic IDE with local models or BYOK. Try Capuchin free with the promo." },
              { "@type": "Offer", name: "Pro", price: "20", priceCurrency: "USD", description: "Unlimited Capuchin, Auto mode, and a $20/mo Claude, Gemini, and ChatGPT allowance." },
              { "@type": "Offer", name: "Pro+", price: "60", priceCurrency: "USD", description: "Auto mode with Max quality, a $60/mo frontier allowance, and a priority queue." },
              { "@type": "Offer", name: "Ultra", price: "200", priceCurrency: "USD", description: "Auto mode with Max quality, a $120/mo frontier allowance, and maximum parallel and background agents." },
            ],
          },
          description:
            "The agentic AI IDE, Code Agent and CLI with its own coding model, Capuchin. Auto mode runs each step on the right model and calls in a frontier model only when the task needs it. Claude, Gemini, and ChatGPT included in one plan. Also runs 100% local or with your own key.",
          url: siteUrl,
          downloadUrl: `${siteUrl}/download`,
          screenshot: `${siteUrl}/og-image.png`,
          featureList: [
            "Capuchin AI model built in (Flash + Reason)",
            "Auto mode: per-step model routing between Capuchin and a frontier model, with every step tagged",
            "Claude, Gemini, and ChatGPT in one plan",
            "IDE, Code Agent desktop app, and CLI on one engine",
            "50+ agent tools with plan mode and parallel sub-agents",
            "Local-first codebase indexing",
            "BYOK and fully local model support",
            "Signed, replayable agent runs",
          ],
        }),
      }}
    />
  );
}

interface FAQItem {
  q: string;
  a: string;
}

export function FAQPageJsonLd({ faqs }: { faqs: FAQItem[] }) {
  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{
        __html: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: faqs.map((faq) => ({
            "@type": "Question",
            name: faq.q,
            acceptedAnswer: {
              "@type": "Answer",
              text: faq.a,
            },
          })),
        }),
      }}
    />
  );
}

interface BlogPostingProps {
  title: string;
  slug: string;
  excerpt?: string | null;
  publishedAt?: string | null;
  author?: string;
}

export function BlogPostingJsonLd({ title, slug, excerpt, publishedAt, author = "MonkeysCode" }: BlogPostingProps) {
  const isPerson = author && author !== "MonkeysCode";
  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{
        __html: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BlogPosting",
          headline: title,
          url: `${siteUrl}/blog/${slug}`,
          description: excerpt || undefined,
          datePublished: publishedAt || undefined,
          author: isPerson
            ? { "@type": "Person", name: author }
            : { "@type": "Organization", name: "MonkeysCode" },
          publisher: {
            "@type": "Organization",
            name: "MonkeysCode",
            logo: {
              "@type": "ImageObject",
              url: `${siteUrl}/favicon-32x32.png`,
            },
          },
        }),
      }}
    />
  );
}

interface BreadcrumbItem {
  name: string;
  url: string;
}

export function BreadcrumbJsonLd({ items }: { items: BreadcrumbItem[] }) {
  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{
        __html: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: items.map((item, i) => ({
            "@type": "ListItem",
            position: i + 1,
            name: item.name,
            // Last item (current page) should not have URL per Google guidelines
            ...(i < items.length - 1 ? { item: `${siteUrl}${item.url}` } : {}),
          })),
        }),
      }}
    />
  );
}
